import {
  CommonLineItemState,
  CommonReducerState,
  MgmtFeeFormState,
  MgmtFeeLineItemState,
  StandardFormState,
  StandardLineItemState,
  ZeroDollarFormState,
  ZeroDollarLineItemState
} from '@/features/line-items/line-item-form/common/form-reducer';
import type { Channel } from 'shared/src/domain/line-item/line-item-channels';
import { ProposalInfo } from '@/features/proposals/proposal-info-context';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { tsOmit } from 'shared/src/utils/object/object-utils';
import { ISODateRange } from 'shared/src/date-string-utils/types';
import { DailyRevenueSpend } from 'shared/src/cube-queries/billable-revenue-query';

export type PacingBlockOrigin = 'saved' | 'pending';
export type PacingBlockClient = {
  dates: ISODateRange;
  budget: number;
  origin: PacingBlockOrigin;
};

export function createInitialStandardState(
  channels: Channel[],
  proposalInfo: ProposalInfo
): StandardFormState {
  const initialCommonState = createInitialCommonState(channels, proposalInfo);
  const formData = {
    ...initialCommonState.formData,
    price: undefined,
    unit_price: undefined,
    marginLocked: false
  };
  return { ...initialCommonState, formData, existing: { type: 'new', data: { ...formData } } };
}

export function createStandardFromExisting(
  channels: Channel[],
  proposalInfo: ProposalInfo,
  lineItem: LineItemTrpcResponse,
  dailyRevenueSpend: DailyRevenueSpend[]
): StandardFormState {
  const formData = convertStandardLineItem(lineItem);
  const dailyBudgetSpent = dailyRevenueSpend.map(d => ({ day: d.day, amount: d.billableRevenue }));
  return {
    mode: 'edit',
    execution_status: lineItem.execution_status,
    touched: new Set<string>(),
    dailyBudgetSpent,
    channels,
    proposalInfo,
    existing: { type: 'existing', id: lineItem.id, data: { ...formData } },
    formData
  };
}

export function createInitialMgmtFeeState(
  channels: Channel[],
  proposalInfo: ProposalInfo
): MgmtFeeFormState {
  const initialCommonState = createInitialCommonState(channels, proposalInfo);
  const formData: MgmtFeeLineItemState = {
    ...initialCommonState.formData,
    price: undefined,
    media_budget: undefined
  };
  return { ...initialCommonState, formData, existing: { type: 'new', data: { ...formData } } };
}

export function createMgmtFeeFromExisting(
  channels: Channel[],
  proposalInfo: ProposalInfo,
  lineItem: LineItemTrpcResponse,
  dailyRevenueSpend: DailyRevenueSpend[]
): MgmtFeeFormState {
  const formData = convertMgmtFeeLineItem(lineItem);
  const dailyBudgetSpent = dailyRevenueSpend.map(d => ({ day: d.day, amount: d.actualSpend }));
  return {
    mode: 'edit',
    execution_status: lineItem.execution_status,
    touched: new Set<string>(),
    dailyBudgetSpent,
    channels,
    proposalInfo,
    existing: { type: 'existing', id: lineItem.id, data: { ...formData } },
    formData
  };
}

export function createInitialZeroState(
  channels: Channel[],
  proposalInfo: ProposalInfo
): ZeroDollarFormState {
  const initialCommonState = createInitialCommonState(channels, proposalInfo);
  const formData: ZeroDollarLineItemState = {
    ...initialCommonState.formData,
    estimated_units: undefined
  };
  return { ...initialCommonState, formData, existing: { type: 'new', data: { ...formData } } };
}

export function createZeroDollarFromExisting(
  channels: Channel[],
  proposalInfo: ProposalInfo,
  lineItem: LineItemTrpcResponse,
  dailyRevenueSpend: DailyRevenueSpend[]
): ZeroDollarFormState {
  const formData = convertZeroDollarLineItem(lineItem);
  const dailyBudgetSpent = dailyRevenueSpend.map(d => ({ day: d.day, amount: d.actualSpend }));
  return {
    mode: 'edit',
    execution_status: lineItem.execution_status,
    touched: new Set<string>(),
    dailyBudgetSpent,
    channels,
    proposalInfo,
    existing: { type: 'existing', id: lineItem.id, data: { ...formData } },
    formData
  };
}

function createInitialCommonState(
  channels: Channel[],
  proposalInfo: ProposalInfo
): CommonReducerState {
  return {
    mode: 'create',
    execution_status: 'no_execution',
    touched: new Set<string>(),
    dailyBudgetSpent: [],
    channels,
    proposalInfo,
    formData: {
      name: '',
      channel_id: null,
      tactic_id: null,
      unit_price_type_id: null,
      planned_unit_cost: undefined,
      dates: proposalInfo.campaign_dates,
      pacing_type: 'lifetime',
      monthly_rollover: false,
      geo: undefined,
      targeting: undefined,
      audience: undefined,
      ad_formats: undefined,
      notes: undefined,
      media_platform_ids: undefined,
      purchase_order_number: undefined,
      pacing_blocks: []
    }
  };
}

function convertStandardLineItem(lineItem: LineItemTrpcResponse): StandardLineItemState {
  return {
    ...convertCommonLineItem(lineItem),
    price: lineItem.price,
    unit_price: lineItem.unit_price ?? undefined,
    marginLocked: false
  };
}

function convertMgmtFeeLineItem(lineItem: LineItemTrpcResponse): MgmtFeeLineItemState {
  return {
    ...convertCommonLineItem(lineItem),
    price: lineItem.price,
    media_budget: lineItem.media_budget || undefined
  };
}

function convertZeroDollarLineItem(lineItem: LineItemTrpcResponse): ZeroDollarLineItemState {
  return {
    ...convertCommonLineItem(lineItem),
    estimated_units: lineItem.estimated_units || undefined
  };
}

function convertCommonLineItem(lineItem: LineItemTrpcResponse): CommonLineItemState {
  return {
    name: lineItem.name,
    channel_id: lineItem.channel_id,
    tactic_id: lineItem.tactic_id,
    unit_price_type_id: lineItem.unit_price_type_id,
    planned_unit_cost: lineItem.planned_unit_cost,
    dates: lineItem.dates,
    pacing_type: lineItem.pacing_type,
    monthly_rollover: lineItem.monthly_rollover,
    geo: lineItem.geo,
    targeting: lineItem.targeting,
    audience: lineItem.audience,
    ad_formats: lineItem.ad_formats,
    notes: lineItem.notes || undefined,
    media_platform_ids: lineItem.media_platform_ids,
    purchase_order_number: lineItem.purchase_order_number || undefined,
    pacing_blocks: lineItem.pacing_details.map(b => ({ ...b, origin: 'saved' }))
  };
}

export function toStandardApiRequest(state: StandardFormState) {
  return {
    ...tsOmit(state.formData, ['pacing_blocks', 'marginLocked']),
    pacing_details: state.formData.pacing_blocks.map(b => tsOmit(b, ['origin'])),
    line_item_type: 'standard'
  };
}

export function toMgmtFeeApiRequest(state: MgmtFeeFormState) {
  return {
    ...tsOmit(state.formData, ['pacing_blocks']),
    pacing_details: state.formData.pacing_blocks.map(b => tsOmit(b, ['origin'])),
    line_item_type: 'management_fee'
  };
}

export function toZeroDollarApiRequest(state: ZeroDollarFormState) {
  return {
    ...tsOmit(state.formData, ['pacing_blocks']),
    pacing_details: state.formData.pacing_blocks.map(b => tsOmit(b, ['origin'])),
    line_item_type: 'zero_dollar',
    price: 0
  };
}
